/**
 * JetPack Builder JavaScript
 * Handles conditional logic, cart integration, and real-time updates
 * Leverages existing theme functionality for seamless integration
 */

class JetPackBuilder {
  constructor() {
    this.config = null;
    this.currentSelections = {
      base: null,
      protein: null,
      toppings: {}, // Changed to object to store quantities: {optionId: quantity}
      dressing: null
    };
    this.floatingCart = null;
    
    this.init();
  }

  generatePackId() {
    // Generate a unique pack ID using timestamp + random string
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 8);
    return `pack-${timestamp}-${randomString}`;
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
    
    // Handle browser back/forward navigation
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        // Page was loaded from cache (back/forward button)
        this.resetEverything();
      }
    });
  }

  setup() {
    this.loadConfig();
    this.initializeElements();
    this.bindEvents();
    this.initializeFloatingCart();
    this.resetEverything(); // Reset everything on setup
  }

  resetEverything() {
    
    // Clear all input selections
    document.querySelectorAll('[data-option-input]').forEach(input => {
      input.checked = false;
    });
    
    // Reset current selections
    this.currentSelections = {
      base: null,
      protein: null,
      toppings: {}, // Changed to object to store quantities: {optionId: quantity}
      dressing: null
    };
    
    // Show all blocks but reset to step 1 only enabled
    const baseBlock = document.querySelector('[data-block-type="base"]');
    const proteinBlock = document.querySelector('[data-block-type="protein"]');
    const toppingsBlock = document.querySelector('[data-block-type="toppings"]');
    const dressingBlock = document.querySelector('[data-block-type="dressing"]');
    
    // Show all blocks
    if (baseBlock) baseBlock.style.display = 'block';
    if (proteinBlock) proteinBlock.style.display = 'block';
    if (toppingsBlock) toppingsBlock.style.display = 'block';
    if (dressingBlock) dressingBlock.style.display = 'block';
    
    // Reset all options - enable all options and hide quantity controls
    document.querySelectorAll('.jp-builder-option').forEach(option => {
      option.classList.remove('jp-builder-option--disabled');
      option.setAttribute('data-available', 'true');
      
      // Re-enable all inputs
      const input = option.querySelector('[data-option-input]');
      if (input) {
        input.disabled = false;
      }
      
      // Hide quantity controls for all options
      const quantityControls = option.querySelector('[data-quantity-controls]');
      if (quantityControls) {
        quantityControls.style.display = 'none';
      }
    });
    
    // Reset to step 1: Enable base, disable all others
    if (baseBlock) baseBlock.classList.remove('jp-builder-block--disabled');
    if (proteinBlock) proteinBlock.classList.add('jp-builder-block--disabled');
    if (toppingsBlock) toppingsBlock.classList.add('jp-builder-block--disabled');
    if (dressingBlock) dressingBlock.classList.add('jp-builder-block--disabled');
    
    // Reset counters
    document.querySelectorAll('[data-selected-count]').forEach(counter => {
      counter.textContent = '0';
    });
    
    // Hide all selected ingredients displays
    document.querySelectorAll('[data-selected-ingredients], [data-selected-toppings]').forEach(display => {
      display.style.display = 'none';
    });
    
    // Update all displays
    this.updatePricing();
    this.updateNutrition();
    this.updateAddToCartButton();
    this.updateFloatingCart();
  }

  clearPreviousSelections() {
    // Legacy method - now just calls resetEverything
    this.resetEverything();
  }

  loadConfig() {
    // Simple config without JSON matrix
    this.config = { 
      currency: 'USD', 
      moneyFormat: '${{amount}}' 
    };
    
    // Get money format from theme if available
    if (window.theme && window.theme.moneyFormat) {
      this.config.moneyFormat = window.theme.moneyFormat;
    }
  }

  initializeElements() {
    this.form = document.querySelector('[data-builder-form]');
    this.blocks = document.querySelectorAll('.jp-builder-block');
    this.addToCartButton = document.querySelector('[data-add-to-cart]');
    this.totalPriceElement = document.querySelector('[data-total-price]');
    this.totalProteinElement = document.querySelector('[data-total-protein]');
    this.totalCaloriesElement = document.querySelector('[data-total-calories]');
    
    // Show all blocks but disable all except step 1
    const baseBlock = document.querySelector('[data-block-type="base"]');
    const proteinBlock = document.querySelector('[data-block-type="protein"]');
    const toppingsBlock = document.querySelector('[data-block-type="toppings"]');
    const dressingBlock = document.querySelector('[data-block-type="dressing"]');
    
    // Show all blocks
    if (baseBlock) baseBlock.style.display = 'block';
    if (proteinBlock) proteinBlock.style.display = 'block';
    if (toppingsBlock) toppingsBlock.style.display = 'block';
    if (dressingBlock) dressingBlock.style.display = 'block';
    
    // Disable all except base (step 1)
    if (proteinBlock) proteinBlock.classList.add('jp-builder-block--disabled');
    if (toppingsBlock) toppingsBlock.classList.add('jp-builder-block--disabled');
    if (dressingBlock) dressingBlock.classList.add('jp-builder-block--disabled');
  }

  bindEvents() {
    // Option selection events
    document.addEventListener('change', (e) => {
      if (e.target.matches('[data-option-input]')) {
        this.handleOptionChange(e.target);
      }
    });

    // Prevent keyboard activation of disabled options
    document.addEventListener('keydown', (e) => {
      if (e.key === ' ' || e.key === 'Enter') {
        const focusedElement = document.activeElement;
        if (focusedElement && focusedElement.matches('[data-option-input]')) {
          const option = focusedElement.closest('.jp-builder-option');
          if (option && (option.classList.contains('jp-builder-option--disabled') || focusedElement.disabled)) {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }
        }
      }
    });



    // Form submission
    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleAddToCart();
      });
    }

    // Add to cart button click
    if (this.addToCartButton) {
      this.addToCartButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleAddToCart();
      });
    }

    // Disabled block and option click events for tooltips
    document.addEventListener('click', (e) => {
      // Check if clicked element is a disabled option
      const clickedOption = e.target.closest('.jp-builder-option--disabled');
      if (clickedOption) {
        e.preventDefault();
        e.stopPropagation();
        this.showTooltip(e, 'Not available for selected base');
        return false;
      }
      
      // Check if clicked element is an option within a disabled block
      const clickedBlockOption = e.target.closest('.jp-builder-option');
      const disabledBlock = e.target.closest('.jp-builder-block--disabled');
      
      if (clickedBlockOption && disabledBlock) {
        e.preventDefault();
        e.stopPropagation();
        this.showTooltip(e, 'Select previous ingredient');
        return false;
      }
    });
  }

  handleOptionChange(input) {
    const option = input.closest('.jp-builder-option');
    const block = input.closest('.jp-builder-block');
    const blockType = block.getAttribute('data-block-type');
    const optionId = option.getAttribute('data-option-id');

    // Prevent selection of disabled options
    if (option.classList.contains('jp-builder-option--disabled') || input.disabled) {
      input.checked = false;
      return;
    }

    if (input.type === 'radio') {
      this.currentSelections[blockType] = optionId;
      this.updateBlockCounter(block, blockType);
    } else if (input.type === 'checkbox') {
      const maxSelections = parseInt(block.getAttribute('data-max-selections') || '3');
      
      if (input.checked) {
        // Count current total quantities
        const currentTotalQty = Object.values(this.currentSelections[blockType]).reduce((sum, qty) => sum + qty, 0);
        if (currentTotalQty < maxSelections) {
          this.currentSelections[blockType][optionId] = 1; // Start with quantity 1
          this.showQuantityControls(option, optionId);
        } else {
          input.checked = false;
          this.showMaxSelectionsMessage(block, maxSelections);
          return;
        }
      } else {
        delete this.currentSelections[blockType][optionId];
        this.hideQuantityControls(option);
      }
      
      this.updateBlockCounter(block, blockType);
      this.updateSelectedToppingsDisplay();
    }

    this.updateConditionalLogic();
    this.updatePricing();
    this.updateNutrition();
    this.updateAddToCartButton();
    this.updateFloatingCart();
    
    // Update selected ingredients display for all block types
    this.updateSelectedIngredientsDisplay(blockType);
  }



  updateConditionalLogic() {
    const baseSelection = this.currentSelections.base;
    const proteinSelection = this.currentSelections.protein;
    
    // Update block accessibility based on previous selections
    this.updateBlockAccessibility();
    
    // Update protein availability based on base selection
    if (baseSelection) {
      this.updateProteinAvailability(baseSelection);
    }
    
    // Update toppings availability based on base selection only
    if (baseSelection) {
      this.updateToppingsAvailability(baseSelection);
    }
    
    // Update dressing availability based on base selection only
    if (baseSelection) {
      this.updateDressingAvailability(baseSelection);
    }
  }

  updateBlockAccessibility() {
    const baseSelection = this.currentSelections.base;
    const proteinSelection = this.currentSelections.protein;
    const toppingsCount = this.currentSelections.toppings ? 
      Object.values(this.currentSelections.toppings).reduce((sum, qty) => sum + qty, 0) : 0;
    
    // Get max toppings requirement
    const toppingsBlock = document.querySelector('[data-block-type="toppings"]');
    const maxToppings = toppingsBlock ? parseInt(toppingsBlock.getAttribute('data-max-selections') || '3') : 3;
    
    // Get all blocks
    const baseBlock = document.querySelector('[data-block-type="base"]');
    const proteinBlock = document.querySelector('[data-block-type="protein"]');
    const dressingBlock = document.querySelector('[data-block-type="dressing"]');
    
    // Step 1: Base block is always enabled (starting point)
    if (baseBlock) {
      baseBlock.classList.remove('jp-builder-block--disabled');
    }
    
    // Step 2: Protein block enabled only if base is selected
    if (proteinBlock) {
      if (baseSelection) {
        proteinBlock.classList.remove('jp-builder-block--disabled');
      } else {
        proteinBlock.classList.add('jp-builder-block--disabled');
      }
    }
    
    // Step 3: Toppings block enabled only if protein is selected
    if (toppingsBlock) {
    if (proteinSelection) {
        toppingsBlock.classList.remove('jp-builder-block--disabled');
      } else {
        toppingsBlock.classList.add('jp-builder-block--disabled');
      }
    }
    
    // Step 4: Dressing block enabled when protein is selected (no toppings requirement)
    if (dressingBlock) {
    if (proteinSelection) {
        dressingBlock.classList.remove('jp-builder-block--disabled');
      } else {
        dressingBlock.classList.add('jp-builder-block--disabled');
      }
    }
  }

  updateProteinAvailability(baseSelection) {
    const proteinBlock = document.querySelector('[data-block-type="protein"]');
    if (!proteinBlock) return;

    const options = proteinBlock.querySelectorAll('.jp-builder-option');
    
    options.forEach(option => {
      const baseRules = option.getAttribute('data-base-rules');
      const input = option.querySelector('[data-option-input]');
      const optionId = option.getAttribute('data-option-id');
      
      let isAvailable = true;
      
      if (baseRules && baseRules.trim() !== '') {
        const allowedBases = baseRules.split(',').map(s => s.trim().toLowerCase());
        isAvailable = allowedBases.includes(baseSelection.toLowerCase());
      }
      
      // Disable/enable option based on availability
      if (!isAvailable) {
        option.classList.add('jp-builder-option--disabled');
        option.setAttribute('data-available', 'false');
        if (input) {
          input.disabled = true;
          input.checked = false;
          // Remove from current selections if no longer available
          if (this.currentSelections.protein === optionId) {
            this.currentSelections.protein = null;
            this.updateBlockCounter(proteinBlock, 'protein');
          }
        }
      } else {
        option.classList.remove('jp-builder-option--disabled');
        option.setAttribute('data-available', 'true');
        if (input) {
          input.disabled = false;
        }
      }
    });
  }

  updateToppingsAvailability(baseSelection) {
    const toppingsBlock = document.querySelector('[data-block-type="toppings"]');
    if (!toppingsBlock) return;

    const proteinSelection = this.currentSelections.protein;
    const options = toppingsBlock.querySelectorAll('.jp-builder-option');
    
    options.forEach(option => {
      const baseRules = option.getAttribute('data-base-rules');
      const input = option.querySelector('[data-option-input]');
      const optionId = option.getAttribute('data-option-id');
      
      let isAvailable = true;
      
      // Check base rules only
      if (baseRules && baseRules.trim() !== '' && baseSelection) {
        const allowedBases = baseRules.split(',').map(s => s.trim().toLowerCase());
        const baseMatches = allowedBases.includes(baseSelection.toLowerCase());
        if (!baseMatches) isAvailable = false;
      }
      
      // Disable/enable option based on availability
      if (!isAvailable) {
        option.classList.add('jp-builder-option--disabled');
        option.setAttribute('data-available', 'false');
        if (input) {
          input.disabled = true;
          input.checked = false;
          // Remove from current selections if no longer available
          delete this.currentSelections.toppings[optionId];
        }
      } else {
        option.classList.remove('jp-builder-option--disabled');
        option.setAttribute('data-available', 'true');
        if (input) {
          input.disabled = false;
        }
      }
    });
    
    // Validate current toppings selections after base change
    this.validateToppingsSelections(toppingsBlock);
    this.updateBlockCounter(toppingsBlock, 'toppings');
  }

  validateToppingsSelections(toppingsBlock) {
    const baseSelection = this.currentSelections.base;
    const currentToppings = Object.keys(this.currentSelections.toppings);
    
    // Check each selected topping to see if it's still valid
    currentToppings.forEach(toppingId => {
      const toppingOption = toppingsBlock.querySelector(`[data-option-id="${toppingId}"]`);
      if (toppingOption) {
        const baseRules = toppingOption.getAttribute('data-base-rules');
        
        let isValid = true;
        
        // Check base rules only
        if (baseRules && baseRules.trim() !== '' && baseSelection) {
          const allowedBases = baseRules.split(',').map(s => s.trim().toLowerCase());
          const baseMatches = allowedBases.includes(baseSelection.toLowerCase());
          if (!baseMatches) isValid = false;
        }
        
        // If topping is no longer valid, deselect it
        if (!isValid) {
          const input = toppingOption.querySelector('[data-option-input]');
          if (input) {
            input.checked = false;
          }
          delete this.currentSelections.toppings[toppingId];
        }
      }
    });
  }

  updateDressingAvailability(baseSelection) {
    const dressingBlock = document.querySelector('[data-block-type="dressing"]');
    if (!dressingBlock) return;

    const options = dressingBlock.querySelectorAll('.jp-builder-option');
    
    options.forEach(option => {
      const baseRules = option.getAttribute('data-base-rules');
      const input = option.querySelector('[data-option-input]');
      const optionId = option.getAttribute('data-option-id');
      
      let isAvailable = true;
      
      // Check base rules only
      if (baseRules && baseRules.trim() !== '' && baseSelection) {
        const allowedBases = baseRules.split(',').map(s => s.trim().toLowerCase());
        const baseMatches = allowedBases.includes(baseSelection.toLowerCase());
        if (!baseMatches) isAvailable = false;
      }
      
      // Disable/enable option based on availability
      if (!isAvailable) {
        option.classList.add('jp-builder-option--disabled');
        option.setAttribute('data-available', 'false');
        if (input) {
          input.disabled = true;
          input.checked = false;
          // Remove from current selections if no longer available
          if (this.currentSelections.dressing === optionId) {
            this.currentSelections.dressing = null;
            this.updateBlockCounter(dressingBlock, 'dressing');
          }
        }
      } else {
        option.classList.remove('jp-builder-option--disabled');
        option.setAttribute('data-available', 'true');
        if (input) {
          input.disabled = false;
        }
      }
    });
  }



  updatePricing() {
    let totalPrice = 0;



    // Calculate prices for each selection
    Object.entries(this.currentSelections).forEach(([blockType, selections]) => {
      if (!selections) return;
      
      if (blockType === 'toppings') {
        // Handle toppings object with quantities
        Object.entries(selections).forEach(([toppingId, quantity]) => {
          const option = document.querySelector(`[data-option-id="${toppingId}"]`);
          if (option) {
            const price = parseInt(option.getAttribute('data-price') || '0');
            totalPrice += price * quantity;
          }
        });
      } else {
        // Handle single selections (base, protein, dressing)
        const selectionsArray = Array.isArray(selections) ? selections : [selections];
        
        selectionsArray.forEach(selectionId => {
          const option = document.querySelector(`[data-option-id="${selectionId}"]`);
          if (option) {
            const price = parseInt(option.getAttribute('data-price') || '0');
            totalPrice += price;
          }
        });
      }
    });



    // Update total price
    if (this.totalPriceElement) {
      this.totalPriceElement.textContent = this.formatMoney(totalPrice);
    }
  }

  updateNutrition() {
    let totalProtein = 0;
    let totalCalories = 0;



    // Calculate nutrition for each selection
    Object.entries(this.currentSelections).forEach(([blockType, selections]) => {
      if (!selections) return;
      
      if (blockType === 'toppings') {
        // Handle toppings object with quantities
        Object.entries(selections).forEach(([toppingId, quantity]) => {
          const option = document.querySelector(`[data-option-id="${toppingId}"]`);
          if (option) {
            // Parse protein and calories as they're now text fields that might contain decimal numbers
            const proteinText = option.getAttribute('data-protein') || '0';
            const caloriesText = option.getAttribute('data-calories') || '0';
            
            // Extract decimal numbers from text (handles formats like "3.5", "20g", "105 cal", etc.)
            const proteinMatch = proteinText.toString().match(/([\d.]+)/);
            const caloriesMatch = caloriesText.toString().match(/([\d.]+)/);
            
            const protein = proteinMatch ? parseFloat(proteinMatch[1]) : 0;
            const calories = caloriesMatch ? parseFloat(caloriesMatch[1]) : 0;
            
            totalProtein += protein * quantity;
            totalCalories += calories * quantity;
          }
        });
      } else {
        // Handle single selections (base, protein, dressing)
        const selectionsArray = Array.isArray(selections) ? selections : [selections];
        
        selectionsArray.forEach(selectionId => {
          const option = document.querySelector(`[data-option-id="${selectionId}"]`);
          if (option) {
            // Parse protein and calories as they're now text fields that might contain decimal numbers
            const proteinText = option.getAttribute('data-protein') || '0';
            const caloriesText = option.getAttribute('data-calories') || '0';
            
            // Extract decimal numbers from text (handles formats like "3.5", "20g", "105 cal", etc.)
            const proteinMatch = proteinText.toString().match(/([\d.]+)/);
            const caloriesMatch = caloriesText.toString().match(/([\d.]+)/);
            
            const protein = proteinMatch ? parseFloat(proteinMatch[1]) : 0;
            const calories = caloriesMatch ? parseFloat(caloriesMatch[1]) : 0;
            
            totalProtein += protein;
            totalCalories += calories;
          }
        });
      }
    });



    // Update nutrition display with proper decimal formatting
    if (this.totalProteinElement) {
      // Round to 1 decimal place for protein
      const roundedProtein = Math.round(totalProtein * 10) / 10;
      this.totalProteinElement.textContent = `${roundedProtein}g`;
    }
    
    if (this.totalCaloriesElement) {
      // Round to whole numbers for calories
      const roundedCalories = Math.round(totalCalories);
      this.totalCaloriesElement.textContent = roundedCalories.toString();
    }
  }

  updateAddToCartButton() {
    // Check required selections: base, protein, and dressing (toppings optional)
    const hasBase = this.currentSelections.base;
    const hasProtein = this.currentSelections.protein;
    const hasDressing = this.currentSelections.dressing;
    
    const hasRequiredSelections = hasBase && hasProtein && hasDressing;
    
    if (this.addToCartButton) {
      this.addToCartButton.disabled = !hasRequiredSelections;
    }
  }

  updateBlockCounter(block, blockType) {
    const counter = block.querySelector('[data-selected-count]');
    if (counter) {
      let count = 0;
      
      if (blockType === 'toppings') {
        // Count total quantities, not just unique toppings
        count = this.currentSelections.toppings ? 
          Object.values(this.currentSelections.toppings).reduce((sum, qty) => sum + qty, 0) : 0;
      } else if (blockType === 'base') {
        count = this.currentSelections.base ? 1 : 0;
      } else if (blockType === 'protein') {
        count = this.currentSelections.protein ? 1 : 0;
      } else if (blockType === 'dressing') {
        count = this.currentSelections.dressing ? 1 : 0;
      }
      
      counter.textContent = count;
    }
  }

  updateSelectedToppingsDisplay() {
    const toppingsDisplay = document.querySelector('[data-selected-toppings]');
    const toppingsList = document.querySelector('[data-selected-toppings-list]');
    
    if (!toppingsDisplay || !toppingsList) return;
    
    if (this.currentSelections.toppings && Object.keys(this.currentSelections.toppings).length > 0) {
      // Convert option IDs to variant names with quantities
      const toppingNames = Object.entries(this.currentSelections.toppings).map(([toppingId, quantity]) => {
        const toppingOption = document.querySelector(`[data-option-id="${toppingId}"]`);
        if (toppingOption) {
          // Get the variant title from the img alt text or label text
          const img = toppingOption.querySelector('.jp-builder-option-img');
          const label = toppingOption.querySelector('.jp-builder-option-label');
          
          let name = '';
          if (img && img.alt) {
            name = img.alt;
          } else if (label) {
            // Try to get text from placeholder or other elements
            const placeholder = toppingOption.querySelector('.jp-builder-option-placeholder span');
            if (placeholder) {
              name = placeholder.textContent.trim();
            }
          }
          
          if (!name) {
            // Fallback: convert option ID to readable format
            name = toppingId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          }
          
          // Add quantity if more than 1
          return quantity > 1 ? `${name} x${quantity}` : name;
        }
        return toppingId;
      });
      
      toppingsList.textContent = toppingNames.join(' + ');
      toppingsDisplay.style.display = 'block';
    } else {
      toppingsDisplay.style.display = 'none';
    }
  }

  updateSelectedIngredientsDisplay(blockType) {
    // Skip toppings as it has its own display function
    if (blockType === 'toppings') {
      return;
    }
    
    const block = document.querySelector(`[data-block-type="${blockType}"]`);
    if (!block) return;
    
    const ingredientsDisplay = block.querySelector('[data-selected-ingredients]');
    const ingredientsList = block.querySelector('[data-selected-ingredients-list]');
    
    if (!ingredientsDisplay || !ingredientsList) return;
    
    const selectedOption = this.currentSelections[blockType];
    
    if (selectedOption) {
      const option = document.querySelector(`[data-option-id="${selectedOption}"]`);
      if (option) {
        let name = this.getOptionDisplayName(option);
        
        ingredientsList.textContent = name;
        ingredientsDisplay.style.display = 'block';
      }
    } else {
      ingredientsDisplay.style.display = 'none';
    }
  }

  showMaxSelectionsMessage(block, maxSelections) {
    // Create and show a temporary message
    const message = document.createElement('div');
    message.className = 'jp-builder-max-message';
    message.textContent = `Maximum ${maxSelections} toppings allowed`;
    message.style.cssText = `
      position: absolute;
      top: -40px;
      left: 50%;
      transform: translateX(-50%);
      background: rgb(var(--color-accent));
      color: white;
      padding: 8px 12px;
      border-radius: 4px;
      font-size: 0.8rem;
      z-index: 10;
      animation: fadeInOut 2s ease;
    `;

    block.style.position = 'relative';
    block.appendChild(message);

    setTimeout(() => {
      if (message.parentNode) {
        message.parentNode.removeChild(message);
      }
    }, 2000);
  }

  showQuantityControls(option, optionId) {
    const quantityControls = option.querySelector('[data-quantity-controls]');
    if (quantityControls) {
      quantityControls.style.display = 'flex';
      
      // Bind quantity events if not already bound
      const plusBtn = quantityControls.querySelector('[data-qty-plus]');
      const minusBtn = quantityControls.querySelector('[data-qty-minus]');
      
      if (plusBtn && !plusBtn.hasAttribute('data-bound')) {
        plusBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.updateQuantity(optionId, 1);
        });
        plusBtn.setAttribute('data-bound', 'true');
      }
      
      if (minusBtn && !minusBtn.hasAttribute('data-bound')) {
        minusBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.updateQuantity(optionId, -1);
        });
        minusBtn.setAttribute('data-bound', 'true');
      }
      
      this.updateQuantityDisplay(option, optionId);
    }
  }

  hideQuantityControls(option) {
    const quantityControls = option.querySelector('[data-quantity-controls]');
    if (quantityControls) {
      quantityControls.style.display = 'none';
    }
  }

  updateQuantity(optionId, change) {
    const blockType = 'toppings'; // Only used for toppings
    const currentQty = this.currentSelections[blockType][optionId] || 0;
    const newQty = currentQty + change;
    
    // Check max selections when increasing
    if (change > 0) {
      const currentTotalQty = Object.values(this.currentSelections[blockType]).reduce((sum, qty) => sum + qty, 0);
      const maxSelections = 3; // Hardcoded max for toppings
      
      if (currentTotalQty >= maxSelections) {
        // Show max selections message
        const toppingsBlock = document.querySelector('[data-block-type="toppings"]');
        if (toppingsBlock) {
          this.showMaxSelectionsMessage(toppingsBlock, maxSelections);
        }
        return; // Don't increase
      }
    }
    
    // Get the option element
    const option = document.querySelector(`[data-option-id="${optionId}"]`);
    const input = option ? option.querySelector('[data-option-input]') : null;
    
    // If quantity would go to 0 or below, remove the topping entirely
    if (newQty <= 0) {
      delete this.currentSelections[blockType][optionId];
      if (input) {
        input.checked = false;
      }
      this.hideQuantityControls(option);
    } else {
      this.currentSelections[blockType][optionId] = newQty;
      if (option) {
        this.updateQuantityDisplay(option, optionId);
      }
    }
    
    // Update block counter
    const toppingsBlock = document.querySelector('[data-block-type="toppings"]');
    if (toppingsBlock) {
      this.updateBlockCounter(toppingsBlock, blockType);
    }
    
    // Update pricing and other displays
    this.updatePricing();
    this.updateNutrition();
    this.updateSelectedToppingsDisplay();
  }

  updateQuantityDisplay(option, optionId) {
    const quantityDisplay = option.querySelector('[data-qty-display]');
    if (quantityDisplay) {
      const qty = this.currentSelections.toppings[optionId] || 1;
      quantityDisplay.textContent = qty;
    }
  }

  async handleAddToCart() {
    if (!this.validateSelections()) {
      return;
    }

    this.setLoading(true);

    try {
      const cartItems = this.buildCartItems();
      
      // Add all items to cart
      await this.addMultipleItemsToCart(cartItems);
      
      // Redirect to cart page
      window.location.href = '/cart';
      
    } catch (error) {
      console.error('Failed to add to cart:', error);
      alert('Failed to add items to cart. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }

  validateSelections() {
    if (!this.currentSelections.base) {
      alert('Please select a base');
      return false;
    }
    
    if (!this.currentSelections.protein) {
      alert('Please select a protein');
      return false;
    }

    return true;
  }

  buildCartItems() {
    const items = [];
    const packId = this.generatePackId();
    
    // Get Pack a Meal product from section settings
    const packProductId = this.getPackMealProductId();
    if (!packProductId) {
      console.error('Pack a Meal product not configured');
      return items;
    }
    
    // Create ingredient details for line item properties
    const ingredientDetails = [];
    
    // Add base to ingredient details
    if (this.currentSelections.base) {
      const baseOption = document.querySelector(`[data-option-id="${this.currentSelections.base}"]`);
      if (baseOption) {
        const baseName = this.getOptionDisplayName(baseOption);
        ingredientDetails.push(`Base: ${baseName}`);
        
        // Add base as individual item with pack properties
        items.push({
          id: baseOption.getAttribute('data-variant-id'),
          quantity: 1,
          properties: {
            '_pack_id': packId,
            '_pack_ingredient': 'Base',
            '_ingredient_name': baseName,
            '_original_qty': '1'
          }
        });
      }
    }

    // Add protein to ingredient details
    if (this.currentSelections.protein) {
      const proteinOption = document.querySelector(`[data-option-id="${this.currentSelections.protein}"]`);
      if (proteinOption) {
        const proteinName = this.getOptionDisplayName(proteinOption);
        ingredientDetails.push(`Protein: ${proteinName}`);
        
        // Add protein as individual item with pack properties
        items.push({
          id: proteinOption.getAttribute('data-variant-id'),
          quantity: 1,
          properties: {
            '_pack_id': packId,
            '_pack_ingredient': 'Protein',
            '_ingredient_name': proteinName,
            '_original_qty': '1'
          }
        });
      }
    }

    // Handle toppings logic
    const toppingsCount = this.currentSelections.toppings ? 
      Object.values(this.currentSelections.toppings).reduce((sum, qty) => sum + qty, 0) : 0;
    
    if (toppingsCount === 0) {
      // No toppings selected - add "No Toppings" variant
      items.push({
        id: '42516269793385',
        quantity: 1,
        properties: {
          '_pack_id': packId,
          '_pack_ingredient': 'Toppings',
          '_ingredient_name': 'No Toppings',
          '_original_qty': '1'
        }
      });
      ingredientDetails.push('Toppings: No Toppings');
    } else if (toppingsCount < 3) {
      // Less than 3 toppings - add "Partial Toppings" variant
      items.push({
        id: '42516269826153',
        quantity: 1,
        properties: {
          '_pack_id': packId,
          '_pack_ingredient': 'Toppings',
          '_ingredient_name': 'Partial Toppings',
          '_original_qty': '1'
        }
      });
      
      // Add individual toppings details for display
      const toppingNames = Object.entries(this.currentSelections.toppings).map(([toppingId, quantity]) => {
        const toppingOption = document.querySelector(`[data-option-id="${toppingId}"]`);
        const name = this.getOptionDisplayName(toppingOption);
        return quantity > 1 ? `${name}` : name;
      }).join(', ');
      ingredientDetails.push(`Toppings: ${toppingNames} (Partial)`);
    } else {
      // 3 or more toppings - add individual toppings
      const toppingNames = [];
      Object.entries(this.currentSelections.toppings || {}).forEach(([toppingId, quantity]) => {
      const toppingOption = document.querySelector(`[data-option-id="${toppingId}"]`);
      if (toppingOption) {
          const name = this.getOptionDisplayName(toppingOption);
          toppingNames.push(quantity > 1 ? `${name} x${quantity}` : name);
          
        items.push({
          id: toppingOption.getAttribute('data-variant-id'),
            quantity: quantity,
            properties: {
              '_pack_id': packId,
              '_pack_ingredient': 'Toppings',
              '_ingredient_name': name,
              '_original_qty': quantity.toString()
            }
        });
      }
    });
      ingredientDetails.push(`Toppings: ${toppingNames.join(', ')}`);
    }

    // Add dressing
    if (this.currentSelections.dressing) {
      const dressingOption = document.querySelector(`[data-option-id="${this.currentSelections.dressing}"]`);
      if (dressingOption) {
        const dressingName = this.getOptionDisplayName(dressingOption);
        ingredientDetails.push(`Dressing: ${dressingName}`);
        
        // Add dressing as individual item with pack properties
        items.push({
          id: dressingOption.getAttribute('data-variant-id'),
          quantity: 1,
          properties: {
            '_pack_id': packId,
            '_pack_ingredient': 'Dressing',
            '_ingredient_name': dressingName,
            '_original_qty': '1'
          }
        }); 
      }
    }

    // Add the main Pack a Meal product with all ingredient details
    const packProperties = {
      '_pack_id': packId,
      '_pack_type': 'main',
      '_pack_ingredients': ingredientDetails.join(' | ')
    };
    
    // Add formatted ingredients (clean format without numbering)
    packProperties['Base'] = this.currentSelections.base ? this.getOptionDisplayName(document.querySelector(`[data-option-id="${this.currentSelections.base}"]`)) : '';
    packProperties['Protein'] = this.currentSelections.protein ? this.getOptionDisplayName(document.querySelector(`[data-option-id="${this.currentSelections.protein}"]`)) : '';
    
    // Handle toppings display
    if (toppingsCount === 0) {
      packProperties['Toppings'] = 'No Toppings';
    } else if (toppingsCount < 3) {
      const toppingNames = Object.entries(this.currentSelections.toppings).map(([toppingId, quantity]) => {
        const toppingOption = document.querySelector(`[data-option-id="${toppingId}"]`);
        const name = this.getOptionDisplayName(toppingOption);
        return quantity > 1 ? `${name} x${quantity}` : name;
      }).join(', ');
      packProperties['Toppings'] = `${toppingNames} (Partial)`;
    } else {
      const toppingNames = Object.entries(this.currentSelections.toppings || {}).map(([toppingId, quantity]) => {
        const toppingOption = document.querySelector(`[data-option-id="${toppingId}"]`);
        const name = this.getOptionDisplayName(toppingOption);
        return quantity > 1 ? `${name} x${quantity}` : name;
      }).join(', ');
      packProperties['Toppings'] = toppingNames;
    }
    
    packProperties['Dressing'] = this.currentSelections.dressing ? this.getOptionDisplayName(document.querySelector(`[data-option-id="${this.currentSelections.dressing}"]`)) : '';
    
    // Add custom pack name if provided
    const customPackName = this.getCustomPackName();
    if (customPackName) {
      packProperties['Pack Name'] = customPackName;
    }

    // Create the main Pack a Meal product item
    const packItem = {
      id: packProductId,
      quantity: 1,
      properties: packProperties
    };

    // Return ingredients first, then pack item last (so pack shows at top of cart page)
    return [...items, packItem].filter(item => item.id);
  }

  findVariantByTitle(variantTitle, block) {
    // Find a variant option by matching the variant title
    const options = block.querySelectorAll('[data-option-id]');
    console.log('Searching for variant title:', variantTitle);
    console.log('Available options in block:', options.length);
    
    for (const option of options) {
      const img = option.querySelector('.jp-builder-option-img');
      const placeholder = option.querySelector('.jp-builder-option-placeholder span');
      
      let title = '';
      if (img && img.alt) {
        title = img.alt;
      } else if (placeholder) {
        title = placeholder.textContent.trim();
      }
      
      console.log('Checking variant title:', title, 'against:', variantTitle);
      
      if (title.toLowerCase() === variantTitle.toLowerCase()) {
        console.log('Found matching variant!');
        return option;
      }
    }
    
    // Log all available titles for debugging
    console.log('All available variant titles:');
    options.forEach(option => {
      const img = option.querySelector('.jp-builder-option-img');
      const placeholder = option.querySelector('.jp-builder-option-placeholder span');
      let title = '';
      if (img && img.alt) {
        title = img.alt;
      } else if (placeholder) {
        title = placeholder.textContent.trim();
      }
      console.log('- Available:', title);
    });
    
    return null;
  }

  getBlockSetting(blockType, settingName) {
    // Get schema setting value from the section blocks
    // This is a simplified approach - in a real implementation, you'd need to access
    // the actual schema settings. For now, we'll use defaults.
    
    if (blockType === 'toppings') {
      if (settingName === 'no_toppings_variant') {
        return 'No Toppings'; // Default value from schema
      } else if (settingName === 'partial_toppings_variant') {
        return 'Partial Toppings'; // Default value from schema
      }
    }
    
    return null;
  }

  getPackMealProductId() {
    // Get the Pack a Meal product ID from section settings
    const section = document.querySelector('[data-section-id]');
    if (section) {
      const packProduct = section.getAttribute('data-pack-meal-product');
      if (packProduct) {
        return packProduct;
      }
    }
    
    // Fallback: Try to find it in global settings or return null
    // You'll need to replace this with the actual Pack a Meal product's first variant ID
    return null; // Replace with actual Pack a Meal variant ID when you have it
  }

  getOptionDisplayName(option) {
    if (!option) return '';
    
    // Try to get name from image alt text
    const img = option.querySelector('.jp-builder-option-img');
    if (img && img.alt) {
      return img.alt;
    }
    
    // Try to get name from placeholder text
    const placeholder = option.querySelector('.jp-builder-option-placeholder span');
    if (placeholder) {
      return placeholder.textContent.trim();
    }
    
    // Fallback: format the option ID
    const optionId = option.getAttribute('data-option-id');
    return optionId ? optionId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '';
  }

  getCustomPackName() {
    // Get custom pack name from input field
    const packNameInput = document.querySelector('[data-pack-name-input]');
    if (packNameInput && packNameInput.value.trim()) {
      return packNameInput.value.trim();
    }
    return '';
  }

  async addMultipleItemsToCart(items) {
    const formData = new FormData();
    
    // Add items as array with properties
    items.forEach((item, index) => {
      formData.append(`items[${index}][id]`, item.id);
      formData.append(`items[${index}][quantity]`, item.quantity);
      
      // Add line item properties if they exist
      if (item.properties) {
        Object.entries(item.properties).forEach(([key, value]) => {
          formData.append(`items[${index}][properties][${key}]`, value);
        });
      }
    });

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error('Failed to add items to cart');
    }

    return response.json();
  }

  setLoading(isLoading) {
    if (this.addToCartButton) {
      this.addToCartButton.disabled = isLoading;
      this.addToCartButton.textContent = isLoading ? 'Adding to Cart...' : 'Add to Cart';
    }
    
    const floatingButton = document.querySelector('[data-floating-add-to-cart]');
    if (floatingButton) {
      floatingButton.disabled = isLoading;
      floatingButton.textContent = isLoading ? 'Adding...' : 'Add to Cart';
    }
  }

  formatMoney(price) {
    if (!this.config || !this.config.moneyFormat) {
      return `$${(price / 100).toFixed(2)}`;
    }

    return this.config.moneyFormat.replace('{{amount}}', (price / 100).toFixed(2));
  }

  // Floating Cart Integration
  initializeFloatingCart() {
    const floatingAddToCart = document.querySelector('[data-floating-add-to-cart]');
    if (floatingAddToCart) {
      floatingAddToCart.addEventListener('click', () => {
        this.handleAddToCart();
      });
    }
  }

  updateFloatingCart() {
    // Simple floating cart update - just sync button state
    const floatingAddToCart = document.querySelector('[data-floating-add-to-cart]');
    if (floatingAddToCart && this.addToCartButton) {
      floatingAddToCart.disabled = this.addToCartButton.disabled;
    }
  }

  // Tooltip functionality
  showTooltip(event, message) {
    // Remove any existing tooltips
    const existingTooltip = document.querySelector('.jp-builder-tooltip');
    if (existingTooltip) {
      existingTooltip.remove();
    }
    
    // Create tooltip element
    const tooltip = document.createElement('div');
    tooltip.className = 'jp-builder-tooltip';
    tooltip.textContent = message;
    
    // Position tooltip at click location
    tooltip.style.cssText = `
      position: fixed;
      top: ${event.clientY - 50}px;
      left: ${event.clientX}px;
      transform: translateX(-50%);
      background: #333;
      color: white;
      padding: 8px 12px;
      border-radius: 4px;
      font-size: 14px;
      z-index: 9999;
      pointer-events: none;
      white-space: nowrap;
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
      opacity: 0;
      transition: opacity 0.2s ease;
    `;
    
    // Add to page and animate in
    document.body.appendChild(tooltip);
    requestAnimationFrame(() => {
      tooltip.style.opacity = '1';
    });
    
    // Auto-remove after 2 seconds
    setTimeout(() => {
      if (tooltip.parentNode) {
        tooltip.style.opacity = '0';
        setTimeout(() => {
          if (tooltip.parentNode) {
            tooltip.remove();
          }
        }, 200);
      }
    }, 2000);
  }
}

// Initialize when DOM is ready
new JetPackBuilder();
